import React, { useRef, useCallback, useMemo, useEffect, useState, Fragment } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import MenuItem from "@mui/material/MenuItem";
import { useDispatch } from "react-redux";
import Menu from "@mui/material/Menu";
import { useFormik } from "formik";
import * as yup from "yup";

import COMMON from "common";
import api from "services/api";
import pathnames from "routes/pathnames";
import sanitizeObject from "common/sanitize-object";
import { promptAlertMessage } from "store/slices/alert";
import serveRequestErrors from "common/serve-request-errors";
import queryParamsEntries from "common/query-params-entries";
import capitalizeCharacter from "common/capitalize-character";
import getEmploymentStatusListing from "services/get-employement-status-listing";
import { isDateBeforeToday, formatDatePattern, formatDate, formatDateTime, CALENDAR_FORMAT, formatDateAndTime, isEndDateBeforeStartDate } from "common/calendar";
import AppInput from "components/app-input";
import AppStatus from "components/app-status";
import AppButton from "components/app-button";
import AppEmptyState from "components/app-empty-state";
import AppSearchInput from "components/app-search-input";
import AppSelectInput from "components/app-select-input";
import AppMultiSelectInput from "components/app-multi-select-input";
import AppInviteEmployeeModal from "components/app-invite-employee-modal";
import AppEmployeeModal from "components/pages/user-management/app-employee-modal";
import AppTable, { AppTableCell, AppTableEditIcon, AppTableMoreIcon } from "components/app-table";
import AppProbationCompletedModal from "components/pages/user-management/app-probation-completed-modal";
import AppInternshipCompletedModal from "components/pages/user-management/app-internship-completed-modal";
import addIcon from "assets/images/add-icon.svg";
import viewIcon from "assets/images/view-icon.svg";
import editIcon from "assets/images/edit-icon.svg";
import resendIcon from "assets/images/resend-icon.svg";
import exclamationIcon from "assets/images/exclamation-icon.svg";
import getPositionListing from "services/get-position-listing";
import getJobLevelListing from "services/get-job-level-listing";
import AppCalendarInput from "components/app-calendar-input";
import ERRORS from "common/errors";
import AppCheckbox from "components/app-checkbox";
import exportIcon from "assets/images/pages/user-management/export-icon.svg";
import filterIcon from "assets/images/components/app-multi-select-input/filter-icon.svg";
// import chevronDownIcon from "assets/images/pages/user-management/chevron-down-icon.svg";
import placeholderFormat from "common/placeholder-format";


const calendarDisplayFormat = CALENDAR_FORMAT.DATE_FORMAT + " " + CALENDAR_FORMAT.MONTH_FORMAT + " " + CALENDAR_FORMAT.YEAR_FORMAT;

const PageEmployees = (props) => {
	const tableRef = useRef();
	const [isExporting, setIsExporting] = useState(false);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const searchInputRef = useRef();
	const employeeModalRef = useRef();
	const probationCompletedRef = useRef();
	const internshipCompletedRef = useRef();
	const inviteEmployeeModalRef = useRef();
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedUser, setSelectedUser] = useState({});
	//const [ShowConfirmExport, setShowConfirmExport] = useState(false);
	const [searchParams, setSearchParams] = useSearchParams();
	//prettier-ignore
	const paramsRef = useRef({
		page: parseInt(searchParams.get("page")) || 1,
		sort: searchParams.get("sort") || "",
		employmentStatus: searchParams.get("employmentStatus") || "",
		email: searchParams.get("email") || "",
		name: searchParams.get("name") || "",
		nickname: searchParams.get("nickname") || "",
		position: searchParams.get("position") || "",
		id: searchParams.get("id") || "",
		level: searchParams.get("level") || "",
		joinStartDate: searchParams.get("joinStartDate") || "",
		joinEndDate: searchParams.get("joinEndDate") || "",
		lastEmploymentStartDate: searchParams.get("lastEmploymentStartDate") || "",
		lastEmploymentEndDate: searchParams.get("lastEmploymentEndDate") || "",
	});
	const [data, setData] = useState({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });
	const [employmentStatusOptions, setEmploymentStatusOptions] = useState([]);
	const initialValues = useMemo(() => ({ employmentStatus: [], email: "", name: "", nickname: "", position: "", id: "", level: "", joinStartDate: "", joinEndDate: "", lastEmploymentStartDate: "", lastEmploymentEndDate: "" }), []);
	const memoSetSearchParams = useRef(setSearchParams);
	const cancelRequest = useMemo(() => props.onHandleCancelRequest, [props.onHandleCancelRequest]);
	const isEmptyState = useMemo(() => COMMON.TABLE_REQUEST_STATUS.INSTANCE === data.status && !paramsRef.current.email && !paramsRef.current.name && !paramsRef.current.nickname && !paramsRef.current.email && !paramsRef.current.employmentStatus
		&& !paramsRef.current.position && !paramsRef.current.id && !paramsRef.current.level && !paramsRef.current.joinStartDate && !paramsRef.current.joinEndDate
		&& !paramsRef.current.lastEmploymentStartDate && !paramsRef.current.lastEmploymentEndDate, [data.status]);
	const isResendInviation = useMemo(() => {
		switch (selectedUser.employmentStatus) {
			case COMMON.STATUS_ID.RESIGNED:
			case COMMON.STATUS_ID.DISMISSAL:
			case COMMON.STATUS_ID.TERMINATION:
				return true;
			default:
				return false;
		}
	}, [selectedUser]);
	const formik = useFormik({
		initialValues,
		validationSchema: yup.object({
			joinStartDate: yup.string().test("beforeEndDate", ERRORS.START_DATE, function (value) {
				if (!value || !this.parent.joinEndDate)
					return true;

				return isDateBeforeToday(value, this.parent.joinEndDate);
			}),
			joinEndDate: yup.string().test("afterStartDate", ERRORS.END_DATE, function (value) {
				if (!value || !this.parent.joinStartDate)
					return true;

				return isDateBeforeToday(this.parent.joinStartDate || new Date(), value);
			}),
			lastEmploymentStartDate: yup.string().test("beforeEndDate", ERRORS.START_DATE, function (value) {
				if (!value || !this.parent.lastEmploymentEndDate)
					return true;

				return isDateBeforeToday(value, this.parent.lastEmploymentEndDate);
			}),
			lastEmploymentEndDate: yup.string().test("afterStartDate", ERRORS.END_DATE, function (value) {
				if (!value || !this.parent.lastEmploymentStartDate)
					return true;

				return isDateBeforeToday(this.parent.lastEmploymentStartDate || new Date(), value);
			}),
		}),
		onSubmit: (values) => {
			onHandleSubmitSearch(values);
		},
	});

	//prettier-ignore
	const onHandleJoinStartDate = useCallback((name, value) => {
		formik.setFieldValue(name, value);

		if (isEndDateBeforeStartDate(value, formik.values.joinEndDate)) formik.setFieldValue("joinEndDate", "");
	}, [formik]);

	//prettier-ignore
	const onHandleLastEmploymentStartDate = useCallback((name, value) => {
		formik.setFieldValue(name, value);

		if (isEndDateBeforeStartDate(value, formik.values.lastEmploymentEndDate)) formik.setFieldValue("lastEmploymentEndDate", "");
	}, [formik]);

	const setValues = useMemo(() => formik.setValues, [formik]);

	const advanceSearchValues = useMemo(() => {
		const params = queryParamsEntries(searchParams);
		let values = {
			employmentStatus: params.employmentStatus?.split(",")?.map((o) => capitalizeCharacter(o.split("_").join(" "))).join(", "), id: params.id, email: params.email, name: params.name
			, nickname: params.nickname, position: params.position, level: params.level, phone: params.phone, joinStartDate: params.joinStartDate, joinEndDate: params.joinEndDate
			, lastEmploymentStartDate: params.lastEmploymentStartDate, lastEmploymentEndDate: params.lastEmploymentEndDate
		};

		return values;
	}, [searchParams]);

	const onHandleGetList = useCallback(async () => {
		let response = null;

		setData({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });

		try {
			const payload = { ...paramsRef.current, size: 10 };

			memoSetSearchParams.current(sanitizeObject(payload));

			payload.page = paramsRef.current.page - 1;

			if (payload.joinStartDate) {
				payload.joinStartDate = formatDateAndTime(new Date(payload.joinStartDate));
			}

			if (payload.joinEndDate) {
				payload.joinEndDate = formatDateAndTime(new Date(payload.joinEndDate));
			}

			if (payload.lastEmploymentStartDate) {
				payload.lastEmploymentStartDate = formatDateAndTime(new Date(payload.lastEmploymentStartDate));
			}

			if (payload.lastEmploymentEndDate) {
				payload.lastEmploymentEndDate = formatDateAndTime(new Date(payload.lastEmploymentEndDate));
			}

			response = await api.get.employee.list(sanitizeObject({ ...payload }));
		} catch (error) {
			serveRequestErrors(error);
		}

		if (response) {
			setData((prev) => ({
				...prev,
				page: paramsRef.current.page,
				prev: !response.first,
				next: !response.last,
				items: response.content,
				total: response.totalPages,
				totalElements: response.totalElements,
				offset: response?.pageable?.offset || 0,
				status: response?.pageable,
			}));
		}
	}, []);

	const onHandleReinviteUser = async () => {
		let response = null;

		try {
			onhandleCloseMenu();

			const payload = { email: selectedUser.email };

			await api.post.employee.reinviteTerminatedUser(payload);
			response = true;
		} catch (error) {
			serveRequestErrors(error);
		}

		if (response) {
			onHandleGetList();
			dispatch(promptAlertMessage({ message: "An invitation has been resent successfully" }));
		}
	};

	const onHandleSubmitSearch = (values) => {
		searchInputRef.current.onhandleCloseAdvanceSearch();

		paramsRef.current = { ...paramsRef.current, page: 1, ...values, employmentStatus: values.employmentStatus.join(",") };

		onHandleGetList();
	};

	const onHandleRemoveField = (field) => {
		formik.setFieldValue(field, field === "employmentStatus" ? [] : "");

		paramsRef.current = { ...paramsRef.current, page: 1, [field]: "" };

		onHandleGetList();
	};

	const onHandleResetSearch = () => {
		formik.setValues(formik.initialValues);
		setVisibleColumns(columns.map((col) => col.accessor).filter(accessor => accessor !== "email" && accessor !== "status"));

		paramsRef.current = { page: 1, sort: "", ...formik.initialValues, employmentStatus: "" };

		onHandleGetList();
	};

	const onHandlePagination = (event) => {
		const control = event.currentTarget?.getAttribute("data-ctrl");

		if (control) {
			if (control === "prev") {
				if (paramsRef.current.page <= 1) return;
				paramsRef.current.page -= 1;
			} else {
				if (paramsRef.current.page >= data.total) return;
				paramsRef.current.page += 1;
			}
		} else {
			paramsRef.current.page = event.target.value;
		}

		onHandleGetList();
	};

	const onHandleSort = (id, order) => {
		switch (id) {
			case "organisationName":
				paramsRef.current.sort = order ? "o.name," + order : "";
				break;
			default:
				paramsRef.current.sort = order ? id + "," + order : "";
				break;
		}

		paramsRef.current.page = 1;

		onHandleGetList();
	};

	const onHandleTableMenu = (event, { row }) => {
		setSelectedUser(row.original);
		setAnchorEl(event.currentTarget);
	};

	const onhandleCloseMenu = () => {
		setAnchorEl(null);
	};

	const onhandleViewProfile = () => {
		setAnchorEl(null);
		employeeModalRef.current.onHandleShow(selectedUser);
	};

	const onHandleEdit = () => {
		setAnchorEl(null);
		navigate(pathnames.userManagement.employee + selectedUser.email, { state: selectedUser });
	};

	const MenuCell = useCallback((obj) => {
		return <AppTableMoreIcon onClick={(event) => onHandleTableMenu(event, obj)} />;
	}, []);

	const DateJoinedCell = useCallback(({ row }) => {
		return <AppTableCell left value={formatDate(row.original.dateJoined)} />;
	}, []);

	const LastEmploymentDateCell = useCallback(({ row }) => {
		return <AppTableCell left value={formatDatePattern(row.original.lastEmploymentDate)} />;
	}, []);

	const LastModifiedCell = useCallback(({ row }) => {
		return <AppTableCell left value={`${row.original.lastModifiedByName || "-"}\n${formatDateTime(new Date(row.original.lastModifiedDate))}`} />;
	}, []);

	const PositionCell = useCallback(({ row }) => {
		return <AppTableCell left value={row.original.position || "-"} />;
	}, []);

	const StatusEmplomentCell = useCallback(({ row }) => {
		return <AppStatus status={row.original.employmentStatus} />;
	}, []);

	const ExclamationCell = useCallback(({ row }) => {
		const today = new Date();
		const original = row.original;
		const internshipCompleted = original.employmentStatus === COMMON.STATUS_ID.INTERN && isDateBeforeToday(original.internshipEndDate, today);
		const probationCompleted = original.employmentStatus === COMMON.STATUS_ID.PROBATION && isDateBeforeToday(original.probationEndDate, today);
		const onClick = internshipCompleted ? () => internshipCompletedRef.current?.onHandleShow(original) : () => probationCompletedRef.current?.onHandleShow(original);

		if (internshipCompleted || probationCompleted) return <AppTableEditIcon className="table__exclamation" icon={exclamationIcon} onClick={onClick} />;
		else return null;
	}, []);

	//prettier-ignore
	const columns = useMemo(() => [
		{
			Header: "Employee ID",
			accessor: "employeeId",
			disableSortBy: false,
		},
		{
			Header: "Employee Name",
			accessor: "name",
			disableSortBy: false,
		},
		{
			Header: "Nickname",
			accessor: "nickname",
			disableSortBy: false,
		},
		{
			Header: "Email Address",
			accessor: "email",
			disableSortBy: false,
		},
		{
			Header: "Level",
			accessor: "level",
			disableSortBy: false
		},
		{
			Header: "Position",
			accessor: "position",
			disableSortBy: false,
			Cell: PositionCell,
		},
		{
			Header: "Join Date",
			accessor: "dateJoined",
			disableSortBy: false,
			Cell: DateJoinedCell,
		},
		{
			Header: "Last Employment",
			accessor: "lastEmploymentDate",
			disableSortBy: false,
			Cell: LastEmploymentDateCell,
		},
		{
			Header: "Status",
			accessor: "employmentStatus",
			disableSortBy: false,
			Cell: StatusEmplomentCell,
		},
		{
			Header: "Last Update by",
			accessor: "lastModifiedDate",
			disableSortBy: false,
			Cell: LastModifiedCell,
		},
		{
			Header: "",
			accessor: "**",
			disableSortBy: true,
			Cell: ExclamationCell,
		},
		{
			Header: "",
			accessor: "*",
			disableSortBy: true,
			Cell: MenuCell,
		},
	], [MenuCell, StatusEmplomentCell, LastModifiedCell, PositionCell, ExclamationCell, LastEmploymentDateCell, DateJoinedCell]);

	const [visibleColumns, setVisibleColumns] = useState(columns.map((col) => col.accessor).filter(col => defaultNoDisplayHeaders(col)));

	const onHandleExportAsExcel = useCallback(async () => {
		setIsExporting(true);

		const visibleColumnsFiltered = visibleColumns.filter((col) => col !== "*" && col !== "**");

		const visibleHeaders = columns.filter((col) => visibleColumnsFiltered.includes(col.accessor)).map((col) => col.Header);

		const values = { ...paramsRef.current }

		let response = null;
		let fileName = null;

		let sortFilter = values.sort;
		if (sortFilter) {
			const [sortColumn] = sortFilter.split(",");
			if (!visibleColumnsFiltered.includes(sortColumn)) {
				sortFilter = null;
			}
		}


		try {
			const payload = {
				headers: visibleHeaders,
				columnFilters: visibleColumnsFiltered,
				searchFilters: {
					employeeId: values.id,
					name: values.name,
					nickname: values.nickname,
					email: values.email,
					employmentStatus: values.employmentStatus,
					position: values.position,
					level: values.level,
					joinStartDate: values.joinStartDate,
					joinEndDate: values.joinEndDate,
					lastEmploymentStartDate: values.lastEmploymentStartDate,
					lastEmploymentEndDate: values.lastEmploymentEndDate,
				},
				sortFilter: sortFilter,
			}

			const transformResponse = (data, headers) => {
				fileName = headers?.["content-disposition"]?.split("attachment; filename=")?.[1];

				if (fileName) return data;

				try {
					const jsonResponse = JSON.parse(new TextDecoder().decode(data));
					if (jsonResponse) return jsonResponse;
				} catch (error) {
					return data;
				}
			};

			response = await api.post.employee.exportEmployeeAsExcel(payload, { transformResponse });
		} catch (error) {
			serveRequestErrors(error);
		} finally {
			setIsExporting(false);
		}

		if (response) {
			const a = document.createElement("a");
			document.body.appendChild(a);
			const url = window.URL.createObjectURL(new Blob([response]), { type: "application/octet-stream" });
			a.href = url;
			a.download = fileName;
			a.click();
			setTimeout(() => {
				window.URL.revokeObjectURL(url);
				document.body.removeChild(a);
			}, 1000);
		}

	}, [visibleColumns, columns]);

	const onHandleInviteEmployee = useCallback(() => {
		inviteEmployeeModalRef.current.onHandleShow();
	}, []);

	const toggleColumnVisibility = (accessor) => {
		setVisibleColumns((prev) => {
			if (!prev.includes(accessor)) return [...prev, accessor];

			const next = prev.filter((val) => val !== accessor);
			const hasDataColumn = next.some((val) => val && val !== "*" && val !== "**");

			return hasDataColumn ? next : prev;
		});
	};

	const onHandleShowAdvanceSearch = (event) => {
		if (searchInputRef.current?.onHandleShowAdvanceSearch) {
			searchInputRef.current.onHandleShowAdvanceSearch(event);
		}
	};

	const headerButtons = useMemo(() => {
		const buttons = [
			{ label: "Filter", icon: filterIcon, outline: true, onClick: onHandleShowAdvanceSearch },
			{ label: "Export", icon: exportIcon, outline: true, onClick: onHandleExportAsExcel, disabled: isExporting, showLoadingDots: isExporting },
			{ label: "Invite", icon: addIcon, outline: false, onClick: onHandleInviteEmployee },
		];
		return buttons;
	}, [onHandleInviteEmployee, onHandleExportAsExcel, isExporting]);

	useEffect(() => {
		onHandleGetList();
	}, [onHandleGetList]);

	useEffect(() => {
		const { page, sort, ...res } = paramsRef.current;

		setValues((prev) => ({ ...prev, ...res, employmentStatus: res.employmentStatus ? res.employmentStatus.split(",") : [] }));
	}, [setValues]);

	useEffect(() => {
		getEmploymentStatusListing()
			.then((response) => setEmploymentStatusOptions(response.content))
			.catch(() => {});
	}, []);

	useEffect(() => {
		return () => {
			cancelRequest(COMMON.ENDPOINT_PATH.EMPLOYEE.LIST);
		};
	}, [cancelRequest]);

	const closeModal = () => {
		inviteEmployeeModalRef.current?.onHandleDismiss();
	};

	function defaultNoDisplayHeaders(col) {
        if (col === "email") return false;
        if (col === "nickname") return false;
        if (col === "lastEmploymentDate") return false;
        return true;
    }

	return (
		<div className="page-employees">
			<div className="employees">
				{isEmptyState && (
					<div className="employees__empty-state">
						<AppEmptyState title="No records found" description="You don’t have any records yet" disabledButton={true} />
						<div className="employees__button-container">
							<AppButton type="button" label="invite" onClick={onHandleInviteEmployee} />
							{/* <AppButton type="button" label="Bulk Invite" outline onClick={() => {}} /> */}
						</div>
					</div>
				)}

				{!isEmptyState && (
					<Fragment>
						<AppSearchInput ref={searchInputRef} multiValues={advanceSearchValues} onRemoveField={onHandleRemoveField} buttons={headerButtons}>
							<form className="app-advance-search-form" onSubmit={formik.handleSubmit}>
								<div className="advance-form">
									<div className="advance-form__inputs">
										<span className="advance-form__inputs__toggle-word">Select Columns to Display:</span>
										<div className="advance-form__inputs__checkbox">
											{columns
												.filter((col) => col.accessor && col.accessor !== "*" && col.accessor !== "**")
												.map((col) => (
													<div key={col.accessor}>
														<AppCheckbox
															key={col.accessor}
															name={col.accessor}
															value={visibleColumns.includes(col.accessor)}
															onClick={() => toggleColumnVisibility(col.accessor)}
															label={col.Header}
														/>
													</div>
												))}
										</div>

										<AppInput type="text" name="id" label="Employee ID" placeholder="Enter Employee ID" value={formik.values.id} onChange={formik.handleChange} />

										<AppInput type="text" name="name" label="Employee Name" placeholder="Enter Employee Name" value={formik.values.name} onChange={formik.handleChange} />

										<AppInput type="text" name="nickname" label="Nickname" placeholder="Enter Nickname" value={formik.values.nickname} onChange={formik.handleChange} />

										<AppInput type="text" name="email" label="Email Address" placeholder="Enter Email Address" value={formik.values.email} onChange={formik.handleChange} />

										<AppSelectInput name="level" label="Level" placeholder="Select Level" loadOptions={getJobLevelListing} value={formik.values.level} onChange={formik.handleChange} />

										<AppSelectInput name="position" label="Position" placeholder="Select Position" loadOptions={getPositionListing} value={formik.values.position} onChange={formik.handleChange} />

										<AppMultiSelectInput name="employmentStatus" label="Employment Status" placeholder="Select Employment Status" options={employmentStatusOptions} value={formik.values.employmentStatus} onChange={(values) => formik.setFieldValue("employmentStatus", values)} />

									</div>

									<div className="advance-form__inputs advance-form__employee_detail_search">

										<AppCalendarInput name="joinStartDate" label="Join Start Date" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} value={formik.values.joinStartDate} error={formik.errors.joinStartDate} touched={formik.touched.joinStartDate} onChange={onHandleJoinStartDate} />

										<AppCalendarInput name="joinEndDate" label="Join End Date" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} minDate={formik.values.joinStartDate} value={formik.values.joinEndDate} error={formik.errors.joinEndDate} touched={formik.touched.joinEndDate} onChange={formik.setFieldValue} />

										<AppCalendarInput name="lastEmploymentStartDate" label="Last Employment Start Date" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} value={formik.values.lastEmploymentStartDate} error={formik.errors.lastEmploymentStartDate} touched={formik.touched.lastEmploymentStartDate} onChange={onHandleLastEmploymentStartDate} />

										<AppCalendarInput name="lastEmploymentEndDate" label="Last Employment End Date" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} minDate={formik.values.lastEmploymentStartDate} value={formik.values.lastEmploymentEndDate} error={formik.errors.lastEmploymentEndDate} touched={formik.touched.lastEmploymentEndDate} onChange={formik.setFieldValue} />

									</div>

									<div className="advance-form__button-container">
										<AppButton type="button" label="Clear" outline onClick={onHandleResetSearch} />
										<AppButton type="submit" label="Search" />
									</div>
								</div>
							</form>
						</AppSearchInput>

						{(paramsRef.current.email || paramsRef.current.employmentStatus || paramsRef.current.name || paramsRef.current.nickname || paramsRef.current.position || paramsRef.current.id || paramsRef.current.joinStartDate
							|| paramsRef.current.joinEndDate || paramsRef.current.lastEmploymentStartDate || paramsRef.current.lastEmploymentEndDate
						) && (
								<div className="employees__results">
									<p className="employees__text">{data.totalElements} results found</p>
								</div>
							)}

						<AppTable ref={tableRef} columns={columns.filter(col => visibleColumns.includes(col.accessor))} pages={data} onHandlePagination={onHandlePagination} onHandleSort={onHandleSort} />
					</Fragment>
				)}

				{/* prettier-ignore */}
				<Menu classes={{ root: "app-table-menu" }} anchorEl={anchorEl} open={!!anchorEl} onClose={onhandleCloseMenu} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
					<MenuItem onClick={onhandleViewProfile}><img className="app-table-menu__icon" src={viewIcon} alt="view" />View</MenuItem>
					{!isResendInviation && <MenuItem onClick={onHandleEdit}><img className="app-table-menu__icon" src={editIcon} alt="edit" />Edit</MenuItem>}
					{isResendInviation && <MenuItem onClick={onHandleReinviteUser}><img className="app-table-menu__icon" src={resendIcon} alt="resend" />Resend</MenuItem>}
				</Menu>
			</div>

			<AppEmployeeModal ref={employeeModalRef} />

			<AppInviteEmployeeModal onClose={closeModal} ref={inviteEmployeeModalRef} />

			<AppInternshipCompletedModal ref={internshipCompletedRef} onHandleGetList={onHandleGetList} />

			<AppProbationCompletedModal ref={probationCompletedRef} onHandleGetList={onHandleGetList} />
		</div>
	);
};

export default PageEmployees;
